import moment from 'moment';

const getTimeDifference = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diff = now - created;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(weeks / 4);
    const years = Math.floor(months / 12);

    if (seconds < 60) {
        return 'just now';
    }
    if (minutes < 60) {
        return `${minutes}m ago`;
    }
    if (hours < 24) {
        return `${hours}h ago`;
    }
    if (days < 7) {
        return `${days}d ago`;
    }
    if (weeks < 4) {
        return `${weeks}w ago`;
    }
    if (months < 12) {
        return `${months}mo ago`;
    }
    return `${years}y ago`;
}

// generateOtp() was removed: one time codes are generated, stored and verified on the
// server. A browser generated code proves nothing and was trivially readable.

// Color Groups for consolidation
const colorGroups = {
  'Black': ['#000', '#000000', 'Black', 'Charcoal', 'Ebony', 'B black', 'D gray', 'Gunmetal'],
  'White': ['#fff', '#ffffff', 'White', 'Pearl', 'Pearl 2', 'Ivory', 'Snow', 'Cream 2', 'Opal'],
  'Silver/Gray': ['#c0c0c0', '#a9a9a9', '#808080', 'Silver', 'Silver 2', 'Gray', 'Grey', 'Metallic'],
  'Blue': ['#4E5BA6', '#0000ff', '#000080', '#4169e1', 'Blue', 'Navy', 'Azure', 'Cobalt'],
  'Red': ['#FF0000', '#ff0000', '#8b0000', 'Red', 'Crimson', 'Maroon', 'Wine', 'Bronze'],
  'Green': ['#47CD89', '#669F2A', '#008000', '#006400', 'Green', 'Emerald', 'Olive'],
  'Yellow/Gold': ['#FDB022', '#ffff00', '#ffd700', 'Yellow', 'Gold', 'Amber', 'Champagne'],
  'Brown/Beige': ['#F9DBAF', '#a52a2a', '#d2b48c', 'Brown', 'Beige', 'Tan', 'Camel', 'Khaki'],
  'Purple/Pink': ['Purple', 'Purple 2', 'Pink', 'Lavender', 'Magenta', 'Violet', '#800080', '#ffc0cb'],
  'Orange': ['Orange', '#ffa500', 'Copper', 'Coral', 'Peach'],
  'Multi/Other': ['Multi', 'Other', 'Others', 'Custom']
};

// Representative colors for each group (for display)
const colorGroupRepresentatives = {
  'Black': '#000000',
  'White': '#ffffff',
  'Silver/Gray': '#a9a9a9',
  'Blue': '#0047AB',
  'Red': '#E50000',
  'Green': '#00A651',
  'Yellow/Gold': '#FFD700',
  'Brown/Beige': '#8B4513',
  'Purple/Pink': '#800080',
  'Orange': '#FF7F00',
  'Multi/Other': 'linear-gradient(to right, #E50000, #00A651, #0047AB)'
};

// Create a mapping of all colors to their group
const colorToGroup = {};
Object.entries(colorGroups).forEach(([group, colors]) => {
  colors.forEach(color => {
    colorToGroup[color.toLowerCase()] = group;
  });
});

// Function to get the color group for a given color
const getColorGroup = (color) => {
  if (!color) return 'Multi/Other';
  
  // Check if the color is a direct match in our mapping
  const normalizedColor = color.toLowerCase();
  if (colorToGroup[normalizedColor]) {
    return colorToGroup[normalizedColor];
  }
  
  // For hex colors or any other format, check if it contains any of our color keywords
  for (const [group, colors] of Object.entries(colorGroups)) {
    for (const groupColor of colors) {
      if (normalizedColor.includes(groupColor.toLowerCase())) {
        return group;
      }
    }
  }
  
  return 'Multi/Other';
};

// Update the colorMap to use our color group representatives
const colorMap = {
  'Black': colorGroupRepresentatives['Black'],
  'White': colorGroupRepresentatives['White'],
  'Silver/Gray': colorGroupRepresentatives['Silver/Gray'],
  'Blue': colorGroupRepresentatives['Blue'],
  'Red': colorGroupRepresentatives['Red'],
  'Green': colorGroupRepresentatives['Green'],
  'Yellow/Gold': colorGroupRepresentatives['Yellow/Gold'],
  'Brown/Beige': colorGroupRepresentatives['Brown/Beige'],
  'Purple/Pink': colorGroupRepresentatives['Purple/Pink'],
  'Orange': colorGroupRepresentatives['Orange'],
  'Multi/Other': colorGroupRepresentatives['Multi/Other'],
  // Add specific colors that might be directly referenced
  'Yellow': '#FFD700',
  'Gold': '#DAA520',
  'Brown': '#8B4513',
  'Beige': '#F5F5DC',
  'Tan': '#D2B48C'
};

// Legacy colorMap for backward compatibility
const legacyColorMap = {
  '#000': '#000000',
  '#4E5BA6': '#0047AB',
  '#47CD89': '#00A651',
  '#F97066': '#E50000',
  '#FDB022': '#FFD700',
  '#669F2A': '#669F2A',
  '#F9DBAF': '#8B4513',
  'Orange': '#FF7F00',
  'Purple': '#800080',
  'Pink': '#ffc0cb'
};

const formatDate = (date) => {
    if (!date || date === 'N/A') return 'N/A';
    
    // Try to parse the date with moment
    const formattedDate = moment(date, [
        'YYYY/MM/DD', // Try standard format first
        'YYYY-MM-DD',
        'MM/DD/YYYY',
        'DD/MM/YYYY'
    ]);
    
    // Check if the date is valid
    if (!formattedDate.isValid()) {
        return 'N/A';
    }
    
    return `${formattedDate.format('YYYY/MM/DD')} JST`;
};

// Format date with time when available
const formatDateWithTime = (date, time) => {
    if (!date || date === 'N/A') return formatDate(date);
    
    // Try to parse the date with moment
    const formattedDate = moment(date, [
        'YYYY/MM/DD', // Try standard format first
        'YYYY-MM-DD',
        'MM/DD/YYYY',
        'DD/MM/YYYY'
    ]);
    
    // Check if the date is valid
    if (!formattedDate.isValid()) {
        return 'N/A';
    }
    
    // If time is available, include it
    if (time) {
        return `${formattedDate.format('YYYY/MM/DD')} ${time} JST`;
    }
    
    return `${formattedDate.format('YYYY/MM/DD')} JST`;
};

// Create a sortable timestamp from date and time
const createSortableTimestamp = (date, time) => {
    if (!date) return 0;
    
    try {
        // Parse the date
        const [year, month, day] = date.split('/').map(Number);
        
        // Parse the time if available
        let hours = 0, minutes = 0;
        if (time) {
            const timeParts = time.split(':');
            hours = parseInt(timeParts[0]);
            minutes = parseInt(timeParts[1]);
        }
        
        // Create a timestamp
        const timestamp = new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
        return timestamp;
    } catch (e) {
        console.error("Error creating sortable timestamp:", e);
        return 0;
    }
};

// Chassis codes come from the auction sheets in full-width characters (ＢＣＮＲ３３).
// For display only: filters and saved searches keep the stored value.
const toHalfWidth = (text) =>
    String(text ?? '').replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));

export {
  getTimeDifference,
  colorMap,
  legacyColorMap,
  colorGroups,
  colorGroupRepresentatives,
  getColorGroup,
  formatDate,
  formatDateWithTime,
  createSortableTimestamp,
  toHalfWidth
};